import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    ChangeDetectionStrategy,
} from '@angular/core';
import { SidenavService } from '../../../core/services/sidenav/sidenav.service';
import { SideNavigationIconLink } from '../../../core/models/navigation-icon-link';
import { UserRole, UserRoleLabelMapping } from '../../../core/enums/users/role';
import { CommonModule } from '@angular/common';

// Material components
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import {
    MatSidenavModule,
    MatSidenav,
    MatSidenavContent,
} from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatNavList } from '@angular/material/list';
import { MatRadioModule } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { SidenavItemComponent } from './sidenav-item/sidenav-item.component';
import { SidenavBottomPanelComponent } from './sidenav-bottom-panel/sidenav-bottom-panel.component';
import { MembershipChipComponent } from './membership-chip/membership-chip.component';

import { UserStateService } from '../../../modules/user/shared/services/user-state/user-state.service';
import { map, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { AuthenticatedUser } from '../../../core/models/authenticated-user';
import { PublicAppConfigurationService } from '../../../core/services/public-app-configuration/public-app-configuration.service';

@Component({
    selector: 'app-sidenav',
    templateUrl: './sidenav.component.html',
    styleUrl: './sidenav.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatSidenavModule,
        MatToolbarModule,
        MatNavList,
        MatRadioModule,
        MatFormFieldModule,
        MatSelectModule,
        MatCheckboxModule,
        MatSidenav,
        MatSidenavContent,
        SidenavItemComponent,
        SidenavBottomPanelComponent,
        MembershipChipComponent,
    ],
})
export class SidenavComponent implements OnInit, OnDestroy {
    UserRoleLabelMapping = UserRoleLabelMapping;
    navigationIconLinks!: SideNavigationIconLink[];

    @ViewChild(MatSidenav)
    sidenav!: MatSidenav;

    user: AuthenticatedUser | null = null;

    isMobile = false;
    isCollapsed;

    private readonly destroy$ = new Subject<void>();

    readonly userRoles: UserRole[] = Object.values(UserRole).filter(
        (role) => typeof role === 'number',
    ) as UserRole[];

    constructor(
        private readonly observer: BreakpointObserver,
        private readonly sidenavService: SidenavService,
        private readonly userStateService: UserStateService,
        private readonly publicAppConfigurationService: PublicAppConfigurationService,
    ) {
        this.isCollapsed = this.sidenavService.isSideNavCollapsed();
        this.sidenavService.setSidenavCollapsedState(
            this.sidenavService.isSideNavCollapsed(),
        );
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit() {
        this.setupSubscriptions();
    }

    isUserRoleCustomer(): boolean {
        return this.user?.role === UserRole.Customer;
    }

    isMember(): boolean {
        return this.user?.isMember ?? false;
    }

    toggleSidenav() {
        this.sidenav.open(); // On desktop/tablet, the menu can never be fully closed
        this.isCollapsed = !this.isCollapsed;

        this.sidenavService.setSidenavCollapsedState(this.isCollapsed);
    }

    private setupSubscriptions() {
        this.observer
            .observe([
                Breakpoints.Handset,
                Breakpoints.XSmall,
                Breakpoints.Small,
            ])
            .pipe(takeUntil(this.destroy$))
            .subscribe((screenSize) => {
                this.isMobile = screenSize.matches;
            });

        /**
         * Initializes side navigation links based on the authenticated user and public app configuration.
         *
         * Subscribes to authenticated user changes and ensures to retrieve a new public app configuration if one
         * is not present. Sidenav links are then generated based on the user role and app configuration.
         *
         * Flow:
         * 1. Listen for user state changes (login, logout, masquerading)
         * 2. For each user change, fetch (or refresh) the app configuration.
         * 3. Generate navigation links based on user role and app configuration
         */
        this.userStateService.user$
            .pipe(
                tap((user: AuthenticatedUser | null) => {
                    this.user = user;
                }),
                switchMap((user: AuthenticatedUser | null) =>
                    this.publicAppConfigurationService
                        .getOrRefreshApplicationConfiguration()
                        .pipe(
                            map((publicAppConfiguration) => ({
                                user,
                                publicAppConfiguration,
                            })),
                        ),
                ),
                tap(({ user, publicAppConfiguration }) => {
                    this.navigationIconLinks =
                        this.sidenavService.getSideNavigationIconLinksForRole(
                            user?.role,
                            publicAppConfiguration,
                        );
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }
}
